import React, { useState, useEffect } from 'react';
import {
  generateInstallationGuideEmail,
  generateInstallationSMS,
  getInstallationDashboard,
  getInstallationStatus,
} from '../utils/installationTracker';
import './InstallationTracker.css';

const TEAM_MEMBERS = [
  { name: 'Kyle', email: 'kyle@example.com', phone: '+1-555-0101' },
  { name: 'Carlos', email: 'carlos@example.com', phone: '+1-555-0102' },
  { name: 'Jorge', email: 'jorge@example.com', phone: '+1-555-0103' },
];

/**
 * Installation Tracker Dashboard
 * Send installation guides to team members and track their status
 */
export function InstallationTracker() {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState({});
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, guide

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    const data = await getInstallationStatus();
    if (data) {
      setStatus(getInstallationDashboard(data));
    }
  };

  const sendInstallationEmail = async (member) => {
    setSending(true);

    try {
      const emailHtml = generateInstallationGuideEmail();
      const emailResponse = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: member.email,
          subject: `🚀 Install RoofLeads v1.3 - New Storm Alerts + Notifications`,
          html: emailHtml,
          teamMember: member.name,
        }),
      });

      if (emailResponse.ok) {
        setSent((prev) => ({ ...prev, [member.name]: true }));
        setTimeout(() => {
          setSent((prev) => ({ ...prev, [member.name]: false }));
        }, 5000);
      }
    } catch (err) {
      console.error('Failed to send email:', err);
    } finally {
      setSending(false);
    }
  };

  const sendInstallationSMS = async (member) => {
    setSending(true);

    try {
      const smsText = generateInstallationSMS();
      const smsResponse = await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: member.phone,
          message: smsText,
          teamMember: member.name,
        }),
      });

      if (smsResponse.ok) {
        setSent((prev) => ({ ...prev, [`${member.name}_sms`]: true }));
        setTimeout(() => {
          setSent((prev) => ({ ...prev, [`${member.name}_sms`]: false }));
        }, 5000);
      }
    } catch (err) {
      console.error('Failed to send SMS:', err);
    } finally {
      setSending(false);
    }
  };

  const sendToAll = async () => {
    setSending(true);
    for (const member of TEAM_MEMBERS) {
      await sendInstallationEmail(member);
      // Stagger sends to avoid rate limiting
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    setSending(false);
  };

  if (!status) {
    return <div className="tracker-loading">Loading installation status...</div>;
  }

  return (
    <div className="installation-tracker">
      <div className="tracker-header">
        <h2>🚀 RoofLeads v1.3 Installation Tracker</h2>
        <p>Monitor Kyle, Carlos, Jorge installations & logins</p>
      </div>

      <div className="tracker-tabs">
        <button
          className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={`tab-button ${activeTab === 'send' ? 'active' : ''}`}
          onClick={() => setActiveTab('send')}
        >
          📧 Send Guide
        </button>
        <button
          className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          👁️ Preview Email
        </button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="tracker-content">
          <div className="tracker-summary">
            <div className="summary-stat">
              <span className="stat-label">Installation</span>
              <span className="stat-value">
                {status.summary.installed}/{status.summary.total}
              </span>
              <div className="stat-bar">
                <div
                  className="stat-progress"
                  style={{
                    width: `${status.completionRate.installation}%`,
                  }}
                />
              </div>
            </div>

            <div className="summary-stat">
              <span className="stat-label">Logged In</span>
              <span className="stat-value">
                {status.summary.loggedIn}/{status.summary.total}
              </span>
              <div className="stat-bar">
                <div
                  className="stat-progress"
                  style={{
                    width: `${status.completionRate.loginRate}%`,
                    background: '#2e7d32',
                  }}
                />
              </div>
            </div>

            <div className="summary-stat">
              <span className="stat-label">Pending</span>
              <span className="stat-value" style={{ color: '#f77f00' }}>
                {status.summary.pending}
              </span>
            </div>
          </div>

          <table className="tracker-table">
            <thead>
              <tr>
                <th>Team Member</th>
                <th>Installed</th>
                <th>Logged In</th>
                <th>Install Time</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {status.teamStatus.map((member) => (
                <tr key={member.name}>
                  <td className="member-name">{member.name}</td>
                  <td className="status-cell">{member.installed}</td>
                  <td className="status-cell">{member.loggedIn}</td>
                  <td className="time-cell">
                    {member.installTime
                      ? new Date(member.installTime).toLocaleTimeString()
                      : '—'}
                  </td>
                  <td className="time-cell">
                    {member.lastLogin
                      ? new Date(member.lastLogin).toLocaleTimeString()
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="tracker-actions">
            <button
              className="action-button action-primary"
              onClick={sendToAll}
              disabled={sending}
            >
              {sending ? '📧 Sending...' : '📬 Send to All'}
            </button>
            <button
              className="action-button action-secondary"
              onClick={fetchStatus}
            >
              🔄 Refresh Status
            </button>
          </div>
        </div>
      )}

      {/* Send Guide Tab */}
      {activeTab === 'send' && (
        <div className="tracker-content">
          <div className="send-grid">
            {TEAM_MEMBERS.map((member) => (
              <div key={member.name} className="send-card">
                <h3>{member.name}</h3>
                <p className="member-email">{member.email}</p>
                <p className="member-phone">{member.phone}</p>

                <div className="send-buttons">
                  <button
                    className="send-button send-email"
                    onClick={() => sendInstallationEmail(member)}
                    disabled={sending}
                  >
                    {sent[member.name] ? '✅ Sent!' : '📧 Email Guide'}
                  </button>
                  <button
                    className="send-button send-sms"
                    onClick={() => sendInstallationSMS(member)}
                    disabled={sending}
                  >
                    {sent[`${member.name}_sms`] ? '✅ Sent!' : '💬 SMS'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="tracker-actions">
            <button
              className="action-button action-primary"
              onClick={sendToAll}
              disabled={sending}
            >
              {sending ? '📧 Sending to All...' : '🚀 Send to All (Email + SMS)'}
            </button>
          </div>
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="tracker-content">
          <div className="preview-section">
            <h3>📧 Email Preview:</h3>
            <div
              className="email-preview"
              dangerouslySetInnerHTML={{
                __html: generateInstallationGuideEmail(),
              }}
            />
          </div>

          <div className="preview-section">
            <h3>💬 SMS Preview:</h3>
            <div className="sms-preview">
              <pre>{generateInstallationSMS()}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
